// DESCRIPTION: useDebounce hook — delays a value update until typing pauses.
// DESCRIPTION: Used by list toolbars for live debounced search (events + bookings).

import { useState, useEffect } from '@wordpress/element';

/**
 * Debounce a rapidly-changing value (e.g. a search box input).
 *
 * Returns a value that only updates after the source has been stable for
 * `delay` milliseconds. Used so live search fires one request per pause
 * instead of one per keystroke.
 *
 * @since PBOOKING_SINCE
 *
 * @param {string} value The source value to debounce.
 * @param {number} delay Milliseconds to wait after the last change.
 *
 * @return {string} The debounced value.
 */
export function useDebounce( value, delay = 400 ) {
	const [ debouncedValue, setDebouncedValue ] = useState( value );

	useEffect( () => {
		const handler = setTimeout( () => {
			setDebouncedValue( value );
		}, delay );

		return () => {
			clearTimeout( handler );
		};
	}, [ value, delay ] );

	return debouncedValue;
}

export default useDebounce;
